import React, { useState } from 'react';
import { useBodyScrollLock } from '../utils/scrollLock';
import { Anime, LineNotifyConfig } from '../types/anime';
import { alertWatchLinks, sendLineNotification, SCHEDULE_FROM } from '../services/animeService';
import { formatImageUrl } from '../utils/helpers';
import { BROADCAST_DAYS, TIER_CONFIGS } from '../types/anime';
import { episodeCounts } from './EpisodeProgress';
import { airtimeBadge, newEpisodeBadge } from '../utils/lineAlerts';
import {
  X,
  Bell,
  Send,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Sparkles,
  Clock,
  ExternalLink,
} from 'lucide-react';

interface LineNotifyModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: LineNotifyConfig;
  onSaveConfig: (cfg: LineNotifyConfig) => void;
  sampleAnime?: Anime;
  onNotifyTodaySchedule: () => void;
}

/** What the LINE card looks like (mirrors buildAnimeFlexBubble in the API) */
const LINK_COLOR: Record<string, string> = {
  animeruka: 'bg-orange-600',
  'anime-waku': 'bg-teal-600',
  bilibili: 'bg-sky-600',
  youtube: 'bg-red-600',
  wetv: 'bg-emerald-600',
};

const LineCardPreview: React.FC<{ anime: Anime }> = ({ anime }) => {
  const tier = TIER_CONFIGS[anime.grade];
  const c = episodeCounts(anime);
  const scale = Math.max(c.total, c.aired, c.watched, 1);
  const pct = (n: number) => `${Math.min(100, (n / scale) * 100)}%`;
  const day = BROADCAST_DAYS.find((d) => d.id === anime.broadcastDay);
  const scheduleLine =
    anime.broadcastDay === 'UNKNOWN'
      ? 'ยังไม่มีเวลาฉายแน่นอน'
      : `ทุก${day?.nameTh || ''}${anime.broadcastTime ? ` · ${anime.broadcastTime} น.` : ''}`;
  const scheduleNote = [anime.audioType, anime.scheduleSource ? `ตามตาราง ${SCHEDULE_FROM[anime.scheduleSource]}` : '']
    .filter(Boolean)
    .join(' · ');
  const [mainLink, ...otherLinks] = alertWatchLinks(anime.sources, anime.title);

  return (
    <div className="max-w-[270px] mx-auto bg-[#0f172a] border border-slate-700 rounded-2xl overflow-hidden shadow-xl">
      <div className="relative aspect-[3/4] bg-slate-950">
        <img
          src={formatImageUrl(anime.imageUrl)}
          alt={anime.title}
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover"
        />
        <span
          className={`absolute top-3 left-3 px-3 py-1 rounded-[10px] bg-gradient-to-br ${tier.gradient} text-white text-lg font-bold leading-none`}
        >
          {anime.grade}
        </span>
      </div>
      <div className="p-4 space-y-3.5">
        <span className="inline-block px-2.5 py-1 rounded-full bg-rose-700 text-white text-[10px] font-bold">
          🔔 แจ้งเตือนอนิเมะ
        </span>
        <div>
          <div className="text-base font-bold text-white leading-snug">{anime.title}</div>
          {anime.thaiTitle && !anime.title.includes(anime.thaiTitle) && (
            <div className="text-xs text-slate-400 mt-0.5">{anime.thaiTitle}</div>
          )}
        </div>
        <span
          className={`inline-block px-2.5 py-1 rounded-full bg-gradient-to-br ${tier.gradient} text-white text-[10px] font-bold`}
        >
          🏆 {tier.title} · {tier.subtitle.split(' / ')[0]}
        </span>
        <div className="grid grid-cols-3 divide-x divide-slate-700 rounded-xl bg-slate-800 py-2.5 text-center">
          {[
            ['ดูแล้ว', String(c.watched), c.caughtUp ? 'text-emerald-300' : 'text-fuchsia-300'],
            [c.complete ? 'ฉายจบ' : 'ฉายแล้ว', String(c.aired), c.complete ? 'text-sky-300' : 'text-white'],
            ['ทั้งหมด', c.total ? String(c.total) : '?', 'text-slate-300'],
          ].map(([label, value, color]) => (
            <div key={label}>
              <div className="text-[9px] text-slate-400">{label}</div>
              <div className={`text-2xl font-bold leading-tight ${color}`}>{value}</div>
            </div>
          ))}
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between text-[10px]">
            <span className="text-slate-400">ความคืบหน้า</span>
            <span className={`font-bold ${c.caughtUp ? 'text-emerald-400' : 'text-amber-400'}`}>
              {c.caughtUp ? '✓ ดูทันแล้ว' : c.aired > c.watched ? `ค้างดู ${c.aired - c.watched} ตอน` : 'รอตอนใหม่'}
            </span>
          </div>
          <div className="relative h-2 rounded-full bg-slate-800 overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-amber-500" style={{ width: pct(c.aired) }} />
            <div
              className={`absolute inset-y-0 left-0 ${c.caughtUp ? 'bg-emerald-400' : 'bg-fuchsia-500'}`}
              style={{ width: pct(c.watched) }}
            />
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-xl bg-slate-800 px-3 py-2.5">
          <span className="text-xl">🗓️</span>
          <div>
            <div className="text-xs font-bold text-white">{scheduleLine}</div>
            {scheduleNote && <div className="text-[9px] text-slate-400 mt-0.5">{scheduleNote}</div>}
          </div>
        </div>
      </div>
      {mainLink && (
        <div className="px-3.5 pb-3.5 space-y-2">
          <div
            className={`w-full py-2.5 text-white text-center text-xs font-bold rounded-lg ${LINK_COLOR[mainLink.type] || 'bg-purple-600'}`}
          >
            ▶ ดูบน {mainLink.name}
          </div>
          {otherLinks.length > 0 && (
            <div className="flex gap-2">
              {otherLinks.slice(0, 3).map((l) => (
                <div
                  key={l.type}
                  className={`flex-1 py-1.5 text-white text-center text-[10px] font-bold rounded-lg ${LINK_COLOR[l.type] || 'bg-slate-700'}`}
                >
                  {l.name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const LineNotifyModal: React.FC<LineNotifyModalProps> = ({
  isOpen,
  onClose,
  config,
  onSaveConfig,
  sampleAnime,
  onNotifyTodaySchedule,
}) => {
  const [form, setForm] = useState<LineNotifyConfig>(config);
  const [isTesting, setIsTesting] = useState<'airtime' | 'arrived' | null>(null);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    msg: string;
  } | null>(null);
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onSaveConfig(form);
    onClose();
  };

  const webhookUrl = (form.webhookUrl || '').trim();
  const webhookKind = !webhookUrl
    ? 'none'
    : /^https:\/\/(?:ptb\.|canary\.)?discord(?:app)?\.com\/api\/webhooks\/\d+\/[\w-]+/.test(webhookUrl)
    ? 'discord'
    : /anime-ranker\.pages\.dev/.test(webhookUrl)
    ? 'self'
    : 'other';
  const target =
    form.mode === 'webhook' ? (webhookKind === 'discord' ? 'Discord' : 'Webhook') : 'LINE';

  // Sends the same card the automatic alerts use, so both banners can be compared in the chat
  const handleTestSend = async (kind: 'airtime' | 'arrived') => {
    onSaveConfig(form);
    setIsTesting(kind);
    setTestResult(null);
    const latest = Number(sampleAnime?.latestEp) || 1;
    const res = await sendLineNotification(form, {
      anime: sampleAnime,
      badge: !sampleAnime
        ? undefined
        : kind === 'airtime'
          ? airtimeBadge(sampleAnime)
          : newEpisodeBadge({ anime: sampleAnime, fromEp: latest - 1, toEp: latest }),
    });
    setIsTesting(null);
    if (res.success) {
      setTestResult({
        ok: true,
        msg: `ส่งการ์ดแจ้งเตือนเข้า ${target} สำเร็จแล้ว! เช็คในแชท ${target} ของคุณได้เลย 🎉`,
      });
    } else {
      setTestResult({
        ok: false,
        msg: `ไม่สามารถส่งได้: ${
          res.error ||
          (form.mode === 'webhook' ? 'กรุณาตรวจสอบลิงก์ Webhook' : 'กรุณาตรวจสอบ Channel Access Token')
        }`,
      });
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2.5 sm:p-4 safe-area-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-[#06C755]/40 rounded-2xl sm:rounded-3xl max-w-2xl w-full max-h-[92vh] supports-[height:100dvh]:max-h-[92dvh] flex flex-col shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#06C755]/25 via-emerald-950/60 to-slate-900 px-5 py-4 border-b border-[#06C755]/30 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#06C755] text-slate-950 flex items-center justify-center font-extrabold shadow-lg shadow-[#06C755]/30">
              <Bell className="w-5 h-5 fill-current" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-extrabold text-white">
                ตั้งค่าระบบแจ้งเตือน LINE (LINE Notification Center)
              </h3>
              <p className="text-xs text-[#4ef08f]">
                แจ้งเตือนวันออกอากาศ (YouTube) และแจ้งเตือนทันทีที่ตรวจพบตอนใหม่บน Anime-Waku
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form
          id="line-config-form"
          onSubmit={handleSave}
          className="p-4 sm:p-6 space-y-5 flex-1 overflow-y-auto overscroll-contain"
        >
          {/* Mode Selector */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, mode: 'line_messaging' })}
              className={`p-3 rounded-xl border text-left transition-all ${
                form.mode === 'line_messaging'
                  ? 'bg-[#06C755]/20 border-[#06C755] text-white'
                  : 'bg-slate-950 border-slate-800 text-slate-400'
              }`}
            >
              <div className="text-xs sm:text-sm font-bold text-[#4ef08f]">
                💬 LINE Messaging API (แนะนำ)
              </div>
              <div className="text-[11px] text-slate-300 mt-0.5">
                ส่งการ์ด Flex Message สวยงามพร้อมรูปปกและปุ่มกดดูเข้า LINE Official Account ของคุณ
              </div>
            </button>

            <button
              type="button"
              onClick={() => setForm({ ...form, mode: 'webhook' })}
              className={`p-3 rounded-xl border text-left transition-all ${
                form.mode === 'webhook'
                  ? 'bg-indigo-600/20 border-indigo-400 text-white'
                  : 'bg-slate-950 border-slate-800 text-slate-400'
              }`}
            >
              <div className="text-xs sm:text-sm font-bold text-indigo-300">
                🔗 Custom Webhook / Discord
              </div>
              <div className="text-[11px] text-slate-300 mt-0.5">
                ส่ง JSON เข้า Webhook (เช่น Make.com, n8n, Google Apps Script หรือ Discord)
              </div>
            </button>
          </div>

          {form.mode === 'line_messaging' ? (
            <div className="space-y-3 bg-slate-950/70 p-4 rounded-2xl border border-slate-800">
              <div>
                <label className="block text-xs font-bold text-slate-200 mb-1">
                  1. LINE Channel Access Token (Long-lived)
                </label>
                <input
                  type="password"
                  value={form.channelAccessToken}
                  onChange={(e) =>
                    setForm({ ...form, channelAccessToken: e.target.value })
                  }
                  placeholder="วาง Channel Access Token จาก LINE Developers Console..."
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-xs sm:text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#06C755]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-200 mb-1">
                  2. User ID หรือ Group ID ผู้รับ (หากเว้นว่าง ระบบจะส่งแบบ Broadcast ให้เพื่อนทุกคนของบอท)
                </label>
                <input
                  type="text"
                  value={form.targetId}
                  onChange={(e) => setForm({ ...form, targetId: e.target.value })}
                  placeholder="เช่น U1234567890abcdef... (เว้นว่างไว้เพื่อส่งแบบ Broadcast ได้เลย!)"
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-xs sm:text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#06C755]"
                />
              </div>

              <div className="text-[11px] text-slate-400 bg-slate-900/90 p-2.5 rounded-xl border border-slate-800">
                💡 <strong>วิธีรับ Token ฟรีใน 1 นาที:</strong> เข้าไปที่{' '}
                <a
                  href="https://developers.line.biz/console/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#4ef08f] underline inline-flex items-center gap-0.5"
                >
                  LINE Developers Console <ExternalLink className="w-2.5 h-2.5" />
                </a>{' '}
                → สร้าง Messaging API Channel → ไปที่แท็บ <strong>Messaging API</strong> → กด Issue ที่ช่อง{' '}
                <strong>Channel access token (long-lived)</strong> แล้วนำมาวางด้านบน (แอดบอทเป็นเพื่อนใน LINE แล้วเว้นช่อง User ID ว่างไว้ได้เลย!)
              </div>
            </div>
          ) : (
            <div className="space-y-3 bg-slate-950/70 p-4 rounded-2xl border border-slate-800">
              <label className="block text-xs font-bold text-slate-200 mb-1">
                Webhook URL
              </label>
              <input
                type="url"
                value={form.webhookUrl}
                onChange={(e) => setForm({ ...form, webhookUrl: e.target.value })}
                placeholder="https://discord.com/api/webhooks/..."
                className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-xs sm:text-sm text-white"
              />
              {webhookKind === 'discord' && (
                <div className="text-[11px] text-emerald-300">✓ ลิงก์ Webhook ของ Discord ถูกต้อง — จะส่งเป็นการ์ดพร้อมรูปปก</div>
              )}
              {webhookKind === 'self' && (
                <div className="text-[11px] text-rose-300">
                  ⚠️ นี่คือลิงก์ของเว็บ Anime Ranker เอง ไม่ใช่ Webhook — ให้ใช้ลิงก์ที่คัดลอกจาก Discord (ขึ้นต้นด้วย https://discord.com/api/webhooks/)
                </div>
              )}
              {webhookKind === 'other' && (
                <div className="text-[11px] text-amber-300">
                  ลิงก์นี้ไม่ใช่ของ Discord — จะส่งเป็นข้อมูล JSON ธรรมดา (สำหรับ Make.com, n8n ฯลฯ)
                </div>
              )}
              <div className="text-[11px] leading-relaxed text-slate-400 bg-indigo-950/40 border border-indigo-500/20 rounded-xl p-3">
                <div className="font-bold text-indigo-200 mb-1">วิธีเอาลิงก์ Webhook จาก Discord</div>
                1. เปิดเซิร์ฟเวอร์ Discord ของคุณ → คลิกขวาที่ <strong>ห้องแชท</strong> ที่อยากให้แจ้งเตือน → <strong>แก้ไขช่อง (Edit Channel)</strong>
                <br />2. ไปที่ <strong>การเชื่อมต่อ (Integrations)</strong> → <strong>Webhooks</strong> → <strong>New Webhook</strong>
                <br />3. กด <strong>Copy Webhook URL</strong> แล้วนำมาวางในช่องด้านบน → กด <strong>ทดสอบส่ง</strong>
                <br />
                <span className="text-slate-500">(ต้องเป็นแอดมินหรือมีสิทธิ์ "จัดการ Webhooks" ในเซิร์ฟเวอร์นั้น)</span>
              </div>
            </div>
          )}

          {/* Auto-Check & Trigger Conditions */}
          <div className="bg-slate-950/70 p-4 rounded-2xl border border-slate-800 space-y-3">
            <h4 className="text-xs sm:text-sm font-bold text-white flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-amber-400" />
              <span>เงื่อนไขการตรวจเช็คและแจ้งเตือนอัตโนมัติ</span>
            </h4>
            {config.serverAlerts ? (
              <div className="text-[11px] text-emerald-300 bg-emerald-950/40 border border-emerald-500/25 rounded-xl px-3 py-2">
                ✓ เซิร์ฟเวอร์เช็คและส่งแจ้งเตือนให้ทุก 20 นาที — ปิดเว็บหรือปิดคอมก็ยังแจ้งเตือน
              </div>
            ) : (
              <div className="text-[11px] text-amber-300 bg-amber-950/30 border border-amber-500/25 rounded-xl px-3 py-2">
                กด "บันทึก" เพื่อให้เซิร์ฟเวอร์ส่งแจ้งเตือนให้ตลอด แม้ปิดเว็บ
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <label className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-900 border border-slate-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.notifyOnNewEpFound}
                  onChange={(e) =>
                    setForm({ ...form, notifyOnNewEpFound: e.target.checked })
                  }
                  className="w-4 h-4 accent-[#06C755]"
                />
                <span className="text-xs text-slate-200">
                  แจ้งเตือน LINE ทันทีเมื่อเช็คเว็บแล้วพบตอนใหม่ (เหมาะกับเรื่องใน Anime-Waku ที่ไม่รู้วันลง)
                </span>
              </label>

              <label className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-900 border border-slate-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.notifyOnSchedule}
                  onChange={(e) =>
                    setForm({ ...form, notifyOnSchedule: e.target.checked })
                  }
                  className="w-4 h-4 accent-[#06C755]"
                />
                <span className="text-xs text-slate-200">
                  แจ้งเตือนตามตารางวันออกอากาศ (สำหรับเรื่องที่มีคิวฉายชัดเจน)
                </span>
              </label>
            </div>

            <div className="flex items-center justify-between gap-2 pt-1">
              <span className="text-xs text-slate-300">
                ความถี่ในการเช็คตอนใหม่จากเว็บอัตโนมัติ (ขณะเปิดหน้าเว็บ):
              </span>
              <select
                value={form.autoCheckMinutes}
                onChange={(e) =>
                  setForm({
                    ...form,
                    autoCheckMinutes: parseInt(e.target.value, 10) || 0,
                  })
                }
                aria-label="ความถี่ในการเช็คตอนใหม่อัตโนมัติ"
                className="bg-slate-900 border border-slate-700 text-amber-300 text-xs font-bold rounded-xl px-3 py-1.5"
              >
                <option value={0}>ปิดการเช็คอัตโนมัติ (กดเช็คเอง)</option>
                <option value={15}>ทุกๆ 15 นาที</option>
                <option value={30}>ทุกๆ 30 นาที (แนะนำ)</option>
                <option value={60}>ทุกๆ 1 ชั่วโมง</option>
              </select>
            </div>
          </div>

          {/* Live LINE Flex Message Preview */}
          {sampleAnime && (
            <div className="space-y-2">
              <div className="text-xs font-bold text-slate-400 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#4ef08f]" />
                <span>ตัวอย่างหน้าตาการ์ดแจ้งเตือนที่จะเด้งในแชท LINE ของคุณ:</span>
              </div>

              <LineCardPreview anime={sampleAnime} />
            </div>
          )}

          {/* Test Result Message */}
          {testResult && (
            <div
              className={`p-3 rounded-xl border text-xs flex items-center gap-2 ${
                testResult.ok
                  ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-200'
                  : 'bg-rose-950/60 border-rose-500/40 text-rose-200'
              }`}
            >
              {testResult.ok ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
              )}
              <span>{testResult.msg}</span>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="bg-slate-900 px-5 py-3 border-t border-slate-800 flex flex-wrap items-center justify-between gap-2 shrink-0">
          <div className="flex items-center gap-2">
            {(
              [
                ['airtime', '⏰ ทดสอบ: ถึงเวลาฉาย', 'bg-amber-500/20 hover:bg-amber-500/35 text-amber-200 border-amber-500/40'],
                ['arrived', '✅ ทดสอบ: เว็บอัปแล้ว', 'bg-[#06C755]/20 hover:bg-[#06C755]/35 text-[#4ef08f] border-[#06C755]/40'],
              ] as const
            ).map(([kind, label, colors]) => (
              <button
                key={kind}
                type="button"
                onClick={() => handleTestSend(kind)}
                disabled={isTesting !== null}
                title={`ส่งการ์ดตัวอย่างเข้า ${target} ทันที`}
                className={`px-3 py-2 border rounded-xl text-xs font-bold flex items-center gap-1.5 ${colors}`}
              >
                {isTesting === kind ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                <span>{label}</span>
              </button>
            ))}

            <button
              type="button"
              onClick={() => {
                onSaveConfig(form);
                onNotifyTodaySchedule();
              }}
              className="px-3 py-2 bg-rose-600/20 hover:bg-rose-600/35 text-rose-200 border border-rose-500/30 rounded-xl text-xs font-bold hidden sm:flex items-center gap-1.5"
            >
              <span>📢 ส่งคิวฉายวันนี้เข้า {target}</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-medium"
            >
              ปิด
            </button>
            <button
              type="submit"
              form="line-config-form"
              className="px-5 py-2 bg-[#06C755] hover:bg-[#05b34c] text-slate-950 rounded-xl text-xs sm:text-sm font-extrabold shadow-lg"
            >
              บันทึกตั้งค่า LINE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
